import * as consts from './consts';
import { generateFileUploadError } from './utils';

/**
 * Base State
 */
const initialState = {
  activeTextField: null,
  activeMessage: null,
  activeMultiSelectValues: {},
  currentMessageInput: {
    messageText: '',
    isRedesign: false
  },
  dragImage: null,
  dropzoneFileError: '',
  fullScreenFiles: null,
  projectOwnerId: '-2', // -1 is the automated message userId so we'll use -2 here
  savedDraftJSState: null,
  selectedSpaceId: '-1',
  selectedSpaceSinceDate: null,
  selectedFullScreenFile: null,
  showFooter: false,
  showTypingIndicator: false,
  targetDesignIndex: -1,
  messages: {},
  messengerLoadingState: { loading: false, error: null },
  messengerSendingState: { loading: false, error: null },
  inputFiles: [],
  isOpen: false,
  redesignInProgress: false
};
export const messengerReducerKey = 'messenger';
export const reducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
    // Updating the messenger state from data
    case consts.MESSENGER_ACTION_INITIAL_MESSAGES_LOADED: {
      return {
        ...state
      };
    }

    case consts.MESSENGER_ACTION_NEXT_PAGE_LOADED: {
      return {
        ...state
      };
    }

    case consts.MESSENGER_ACTION_UPDATE_COMPLETE: {
      return {
        ...state
      };
    }

    case consts.MESSENGER_ACTION_SEND_COMPLETE: {
      return {
        ...state
      };
    }

    // Base Dispatch Actions
    case consts.MESSENGER_X_OUT_CLICKED: {
      return {
        ...state,
        isOpen: false
      };
    }

    case consts.MESSENGER_BACK_CLICKED: {
      return {
        ...state,
        selectedSpaceId: '-1',
        selectedSpaceSinceDate: null,
        inputDisabled: false
      };
    }

    case consts.MESSENGER_BAD_FILE_TYPE_MODAL_CLOSE:
    case consts.MESSENGER_BAD_FILE_TYPE_MODAL_CONFIRM: {
      return {
        ...state,
        dropzoneFileError: ''
      };
    }

    case consts.MESSENGER_LOADING_STATE_CHANGE: {
      return {
        ...state,
        messengerLoadingState: payload
      };
    }

    case consts.MESSENGER_SUBMITTED_MULTI_SELECT: {
      return {
        ...state
      };
    }

    case consts.MESSENGER_SELECTED_SPACE_CHANGE: {
      return {
        ...state,
        selectedSpaceId: payload.toString(),
        selectedSpaceSinceDate: null
      };
    }

    case consts.MESSENGER_MULTI_SELECT_CHANGED: {
      return {
        ...state,
        activeMultiSelectValues: {
          ...state.activeMultiSelectValues,
          [payload.addtlInfo.messageId]: {
            ...state.activeMultiSelectValues[payload.addtlInfo.messageId],
            [payload.addtlInfo.messageIndex]: payload.selections
          }
        }
      };
    }

    case consts.MESSENGER_SENDING_STATE_CHANGED: {
      return {
        ...state,
        messengerSendingState: payload
      };
    }

    case consts.MESSENGER_TEXT_CHANGED: {
      const { currentMessageInput } = state;

      return {
        ...state,
        currentMessageInput: {
          ...currentMessageInput,
          ...payload
        }
      };
    }

    case consts.MESSENGER_SET_FULL_SCREEN_FILES: {
      return {
        ...state,
        fullScreenFiles: payload.files,
        selectedFullScreenFile: payload.index
      };
    }

    case consts.MESSENGER_EXIT_FULL_SCREEN_FILES: {
      return {
        ...state,
        fullScreenFiles: null,
        selectedFullScreenFile: null
      };
    }

    case consts.MESSENGER_SELECT_FULL_SCREEN_FILE: {
      return {
        ...state,
        selectedFullScreenFile: payload
      };
    }

    case consts.MESSENGER_SELECT_NEXT_FULL_SCREEN_FILE: {
      const { fullScreenFiles, selectedFullScreenFile } = state;
      const newIndex = (selectedFullScreenFile + 1) % fullScreenFiles.length;
      return {
        ...state,
        selectedFullScreenFile: newIndex
      };
    }

    case consts.MESSENGER_SELECT_LAST_FULL_SCREEN_FILE: {
      const { fullScreenFiles, selectedFullScreenFile } = state;
      let newIndex = selectedFullScreenFile - 1;
      newIndex = newIndex < 0 ? fullScreenFiles.length - 1 : newIndex;
      return {
        ...state,
        selectedFullScreenFile: newIndex
      };
    }

    case consts.MESSENGER_SEND_MESSAGE: {
      return {
        ...state,
        currentMessageInput: {
          messageText: ''
        },
        inputFiles: [],
        activeMultiSelectValues: {}
      };
    }

    case consts.MESSENGER_SET_ACTIVE_TEXT_FIELD: {
      return {
        ...state,
        activeTextField: payload.textField
      };
    }

    case consts.MESSENGER_SET_BAD_FILE_UPLOAD: {
      return {
        ...state,
        dropzoneFileError: generateFileUploadError(payload)
      };
    }

    case consts.MESSENGER_SET_DRAG_IMAGE: {
      return {
        ...state,
        dragImage: payload
      };
    }

    case consts.MESSENGER_SET_OWNER_ID: {
      return {
        ...state,
        projectOwnerId: payload.toString()
      };
    }

    case consts.MESSENGER_SET_INPUT_DISABLED: {
      return {
        ...state,
        inputDisabled: payload
      };
    }

    case consts.MESSENGER_SET_MESSAGES: {
      return {
        ...state,
        messages: {
          ...state.messages,
          ...payload
        }
      };
    }

    case consts.MESSENGER_SET_REDESIGN_IN_PROGRESS: {
      return {
        ...state,
        redesignInProgress: payload
      };
    }

    case consts.MESSENGER_SET_SHOW_FOOTER: {
      return {
        ...state,
        showFooter: payload
      };
    }

    case consts.MESSENGER_SET_SHOW_TYPING_INDICATOR: {
      return {
        ...state,
        showTypingIndicator: payload
      };
    }

    case consts.MESSENGER_SET_INPUT_FILES: {
      return {
        ...state,
        inputFiles: payload
      };
    }

    case consts.MESSENGER_CLEAR_TEXT_FIELD: {
      const { activeTextField } = state;
      if (activeTextField) {
        activeTextField.textContent = '';
      }
      return {
        ...state,
        currentMessageInput: {
          messageText: '',
          isRedesign: false
        }
      };
    }

    case consts.MESSENGER_UPDATE_CONVERSATION: {
      return {
        ...state,
        messages: {
          ...state.messages,
          [payload.spaceId]: payload.conversation
        }
      };
    }

    case consts.MESSENGER_UPDATE_DRAFT_STATE: {
      return {
        ...state,
        savedDraftJSState: payload
      };
    }

    case consts.MESSENGER_UPDATE_LAST_SINCE_DATE: {
      return {
        ...state,
        selectedSpaceSinceDate: payload
      };
    }

    case consts.MESSENGER_UPDATE_TARGET_DESIGN: {
      // This is used by the redesign request flow to determine which
      // design images to show first
      /* const existingConversation = state.messages[payload.spaceId];
      let addtlMessageUpdates = {};
      // Reorder the design cards
      if (existingConversation) {
        addtlMessageUpdates = {
          [payload.spaceId]: existingConversation.makeCopyWithTargetDesignSortedMessages({
            targetDesignIndex: payload.targetDesignIndex
          })
        };
      } */
      return {
        ...state,
        targetDesignIndex: payload.targetDesignIndex
      };
    }

    case consts.OPEN_MESSENGER: {
      return {
        ...state,
        isOpen: true
      };
    }

    default:
      return state;
  }
};
